<script lang="ts" setup>
import { locales } from '@thecompaniesapi/shared/client'

const { companyDomain, companyDomains } = useCompany()
const { locale, setLocale, t } = useI18n()
const actionsStore = useActionsStore()
const colorMode = useColorMode()
const config = useRuntimeConfig()
const head = useLocaleHead()
const listsStore = useListsStore()
const overlays = useOverlays()
const platformStore = usePlatformStore()
const route = useRoute()
const teamsStore = useTeamsStore()
const userStore = useUserStore()

useRecaptchaProvider()

const seo = computed(() => {
  let routeName = String(route.name).split('__')[0]

  if (route.params.feature) {
    routeName += `-${route.params.feature}`
  }

  return {
    description: t(`seo.${routeName}.description`),
    title: t(`seo.${routeName}.title`),
  }
})

watch(
  () => userStore.user,
  (user) => {
    if (user?.colorMode) {
      colorMode.preference = user.colorMode
    }

    if (user?.locale) {
      setLocale(user.locale)
    }
  },
  { immediate: true },
)

watch(
  [companyDomain, companyDomains],
  ([domain, domains]) => {
    if (domains.length) {
      overlays.toggleSlideover({
        opened: true,
        type: 'company',
      })

      return
    }

    if (domain) {
      overlays.toggleSlideover({
        opened: true,
        type: 'company',
      })

      return
    }

    overlays.toggleSlideover({
      opened: false,
      type: 'company',
    })
  },
  { immediate: true },
)

defineShortcuts({
  shift_m: {
    handler: () => {
      toggleColorMode()
    },
  },
})

await callOnce(
  'root',
  async () => {
    await platformStore.fetchMetrics()
    await userStore.fetchUser()
  },
)

useHead(() => {
  // Get all alternate locales
  const alternateLocales = locales.filter(item => item !== locale.value)
  const currentPath = route.path === '/' ? '' : route.path
  const pathWithoutLocale = currentPath.replace(`/${locale.value}`, '') || ''

  return {
    link: [
      {
        href: '/favicon.ico',
        rel: 'icon',
        type: 'image/x-icon',
      },
      {
        href: '/favicon.ico',
        rel: 'shortcut icon',
        type: 'image/x-icon',
      },
      {
        href: '/images/favicons/apple-touch-icon.png',
        rel: 'apple-touch-icon',
        sizes: '180x180',
      },
      ...locales.map(alternateLocale => ({
        href: `${config.public.nuxtUrl}${alternateLocale === 'en' ? '' : `/${alternateLocale}`}${pathWithoutLocale}`,
        hreflang: alternateLocale,
        rel: 'alternate',
      })),
      {
        href: `${config.public.nuxtUrl}${pathWithoutLocale}`,
        hreflang: 'x-default',
        rel: 'alternate',
      },
      {
        href: `${config.public.nuxtUrl}${locale.value === 'en' ? '' : `/${locale.value}`}${pathWithoutLocale}`,
        rel: 'canonical',
      },
    ],
    meta: [
      {
        content: `${config.public.nuxtUrl}${locale.value === 'en' ? '' : `/${locale.value}`}${pathWithoutLocale}`,
        property: 'og:url',
      },
      {
        content: locale.value,
        property: 'og:locale',
      },
      ...alternateLocales.map(alternateLocale => ({
        content: alternateLocale,
        property: 'og:locale:alternate',
      })),
    ],
  }
})

useSeoMeta({
  description: () => seo.value.description,
  title: () => seo.value.title,

  appleMobileWebAppStatusBarStyle: 'black-translucent',
  author: 'The Companies API',
  ogImage: 'https://www.thecompaniesapi.com/og-image.jpg',
  ogSiteName: 'The Companies API',
  ogType: 'website',
  themeColor: '#F7FAFC',
  twitterCard: 'summary_large_image',
  twitterCreator: '@thecompaniesapi',
  twitterSite: '@thecompaniesapi',
})

useEvent(
  'team',
  'list:actions',
  (data) => {
    if (data?.actions?.length && data?.list?.id) {
      listsStore.addListActions({
        actions: data.actions as Action[],
        listId: data.list.id,
      })
    }
  },
)

useEvent(
  'team',
  'team:actions',
  (data) => {
    if (data?.actions?.length) {
      actionsStore.addOrReplaceActions({ actions: data.actions })
    }
  },
)

useEvent(
  'team',
  'team:update-team',
  (data) => {
    if (data?.team && data.team.id) {
      teamsStore.addOrReplaceTeams({ teams: [data.team] })
    }
  },
)
</script>

<template>
  <Html
    class="antialiased"
    :dir="head.htmlAttrs?.dir"
    :lang="head.htmlAttrs?.lang"
  />

  <div id="app">
    <NuxtLoadingIndicator
      color="#5a67d8"
      :throttle="0"
    />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <LayoutAlerts />

    <LayoutBars />

    <LayoutPrompt />

    <LayoutGlobals />

    <LayoutDropzone />
  </div>
</template>

<style lang="postcss">
#app {
  overflow-x: hidden;
}

:root {
  --header-height: 3.5rem;
  --footer-height: 14rem;
}

@screen lg {
  :root {
    --header-height: 4.5rem;
    --footer-height: 6rem;
  }
}

#crisp-chatbox {
  z-index: 20;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}

.size-page {
  min-height: calc(calc(100vh - calc(var(--header-height) + 1.25rem)));
}

:focus-visible {
  outline: none !important;
}

:focus {
  outline: none !important;
}
</style>

<i18n lang="json">
{
  "en": {
    "seo": {
      "auth-extension": {
        "description": "If the process takes too long, feel free to contact us.",
        "title": "Connecting to the extension"
      },
      "auth-login": {
        "description": "Create your account or login in one single click.",
        "title": "Login"
      },
      "auth-logout": {
        "description": "Logging out of the current application.",
        "title": "Logging out…"
      },
      "auth-reset": {
        "description": "Choose a password between 6 and 255 characters long.",
        "title": "Reset Password"
      },
      "auth-verification": {
        "description": "Please wait, it should only take a few seconds.",
        "title": "Verifying your email address…"
      },
      "companies": {
        "description": "Search millions of companies through thousands of filters.",
        "title": "Search Companies"
      },
      "companies-enrichment": {
        "description": "Upload a file with domain names, company names, or social network URLs, and we will enrich them.",
        "title": "Enrich Companies from a File"
      },
      "companies-similarity": {
        "description": "Enter a domain name or a list of domains and discover all companies similar to them.",
        "title": "Find Similar Companies"
      },
      "index": {
        "description": "Search and enrich millions companies, find similar businesses, and get notified about important events. Use our API and data to supercharge your product or marketing tools.",
        "title": "The Companies API - Enrichment API for Companies"
      },
      "pricing": {
        "description": "Our product is going to put your lead generation on autopilot.",
        "title": "Pricing"
      },
      "settings": {
        "description": "Browse your API usage and manage your account.",
        "title": "API Dashboard"
      },
      "settings-profile": {
        "description": "Update you your profile information or your password.",
        "title": "Profile & Security"
      },
      "settings-subscription": {
        "description": "Manage your subscription and your credits",
        "title": "Subscription"
      },
      "settings-team": {
        "description": "Information about your team profile and its members.",
        "title": "Team & Members"
      },
      "settings-webhooks": {
        "description": "Create a webhook to receive a notification for the events you woud like to track.",
        "title": "Your Webhooks"
      }
    }
  },
  "es": {
    "seo": {
      "auth-extension": {
        "description": "Si el proceso tarda demasiado, no dude en contactarnos.",
        "title": "Conectando a la extensión"
      },
      "auth-login": {
        "description": "Cree su cuenta o inicie sesión con un solo clic.",
        "title": "Iniciar sesión"
      },
      "auth-logout": {
        "description": "Cerrando sesión de la aplicación actual.",
        "title": "Cerrando sesión…"
      },
      "auth-reset": {
        "description": "Elija una contraseña entre 6 y 255 caracteres de longitud.",
        "title": "Restablecer contraseña"
      },
      "auth-verification": {
        "description": "Por favor espere, solo debería tomar unos segundos.",
        "title": "Verificando su dirección de correo electrónico…"
      },
      "companies": {
        "description": "Busque millones de empresas a través de miles de filtros.",
        "title": "Buscar empresas"
      },
      "companies-enrichment": {
        "description": "Suba un archivo con nombres de dominio, nombres de empresas o URLs de redes sociales, y los enriqueceremos.",
        "title": "Enriquecer empresas desde un archivo"
      },
      "companies-similarity": {
        "description": "Ingrese un nombre de dominio o una lista de dominios y descubra todas las empresas similares a ellos.",
        "title": "Encontrar empresas similares"
      },
      "index": {
        "description": "Busque y enriquezca millones de empresas, encuentre negocios similares y reciba notificaciones sobre eventos importantes. Use nuestra API y datos para potenciar su producto o herramientas de marketing.",
        "title": "The Companies API - API de enriquecimiento para empresas"
      },
      "pricing": {
        "description": "Nuestro producto pondrá su generación de leads en piloto automático.",
        "title": "Precios"
      },
      "settings": {
        "description": "Explore su uso de API y administre su cuenta.",
        "title": "Panel de control API"
      },
      "settings-profile": {
        "description": "Actualice la información de su perfil o su contraseña.",
        "title": "Perfil y seguridad"
      },
      "settings-subscription": {
        "description": "Administre su suscripción y sus créditos",
        "title": "Suscripción"
      },
      "settings-team": {
        "description": "Información sobre el perfil de su equipo y sus miembros.",
        "title": "Equipo y miembros"
      },
      "settings-webhooks": {
        "description": "Cree un webhook para recibir una notificación de los eventos que desea rastrear.",
        "title": "Sus Webhooks"
      }
    }
  },
  "fr": {
    "seo": {
      "auth-extension": {
        "description": "Si le processus prend trop de temps, n'hésitez pas à nous contacter.",
        "title": "Connexion à l'extension"
      },
      "auth-login": {
        "description": "Créez votre compte ou connectez-vous en un seul clic.",
        "title": "S'identifier"
      },
      "auth-logout": {
        "description": "Déconnexion de l'application en cours.",
        "title": "Déconnexion…"
      },
      "auth-reset": {
        "description": "Choisissez un mot de passe entre 6 et 255 caractères de long.",
        "title": "Réinitialiser mot de passe"
      },
      "auth-verification": {
        "description": "Veuillez patienter, cela ne devrait prendre que quelques secondes.",
        "title": "Vérification de votre adresse email…"
      },
      "companies": {
        "description": "Recherchez des millions d'entreprises grâce à nos milliers de filtres",
        "title": "Rechercher des entreprises"
      },
      "companies-enrichment": {
        "description": "Déposez un fichier avec des noms de domaine, des noms d'entreprise ou des URL de réseaux sociaux, et nous les enrichirons.",
        "title": "Enrichir des entreprises à partir d'un fichier"
      },
      "companies-similarity": {
        "description": "Entrez un nom de domaines ou une liste de domaines et découvrez toutes les entreprises qui leur ressemblent.",
        "title": "Trouver des entreprises similaires"
      },
      "index": {
        "description": "Recherchez et enrichissez des millions d'entreprises, trouvez des entreprises similaires, et soyez informé des événements importants. Utilisez notre API et données pour booster votre produit ou vos outils marketing.",
        "title": "The Companies API - API d'Enrichissement Pour Entreprises"
      },
      "pricing": {
        "description": "Notre produit va mettre votre génération de leads sur pilote automatique.",
        "title": "Tarifs"
      },
      "settings": {
        "description": "Explorez votre utilisation de l'API et gérez votre compte.",
        "title": "Tableau de bord API"
      },
      "settings-profile": {
        "description": "Modifier vos informations de profil ou votre mot de passe.",
        "title": "Profil & Securité"
      },
      "settings-subscription": {
        "description": "Gérez votre abonnement et vos crédits",
        "title": "Abonnement"
      },
      "settings-team": {
        "description": "Information sur votre profil d'équipe et ses membres.",
        "title": "Équipe & Membres"
      },
      "settings-webhooks": {
        "description": "Créez un webhook pour recevoir une notification pour les événements que vous souhaitez suivre.",
        "title": "Vos Webhooks"
      }
    }
  }
}
</i18n>
